import type { BaseOutbox } from '../outbox/base'
import { lister } from '../outbox/depot'
import { lundiDe } from './planning'

// Mesures d'usage pour les essais avec des artisans (critères de sortie de la phase 1 : devis simple en moins de 90
// secondes en médiane, trois devis par semaine). Elles restent sur l'appareil, ne sont jamais envoyées au serveur et
// ne contiennent aucune donnée de devis : seulement des durées et des dates.
//
// Durée de création = du moment où le devis est créé (« + Devis ») au premier PDF généré. La date de création est celle
// que porte l'identifiant (UUID v7), donc rien n'est à mémoriser au départ. Un devis repris le lendemain n'est pas une
// création « d'une traite » : au-delà de 30 minutes il est écarté de la médiane.

export const SEUIL_RAPIDE_MS = 90_000
export const SESSION_MAX_MS = 30 * 60_000

/** Date de création (ms) lue dans un UUID v7 : ses 48 premiers bits. */
export const dateDeCreation = (id: string) => parseInt(id.replace(/-/g, '').slice(0, 12), 16)

const cle = (devisId: string) => `mesure:pdf:${devisId}`

/**
 * À appeler quand un PDF de devis est généré : note l'instant du premier seul. Les copies, nouvelles versions et
 * avenants (qui naissent avec des lignes) ne comptent pas comme des créations à partir de rien.
 */
export async function noterPremierPdf(base: BaseOutbox, devis: { id: string; origineId?: string; avenantDe?: string }, maintenant = Date.now()): Promise<void> {
  if (devis.origineId || devis.avenantDe) return
  if (await base.meta.get(cle(devis.id))) return
  await base.meta.put({ cle: cle(devis.id), valeur: maintenant })
}

export type Mesures = {
  /** Devis dont on connaît la durée de création d'une traite. */
  mesures: number
  medianeMs: number | null
  /** Part (0 à 1) des créations de moins de 90 secondes ; null sans mesure. */
  partRapide: number | null
  /** Devis créés par semaine (lundi → nombre), de la plus ancienne à la semaine en cours. */
  parSemaine: { lundi: string; devis: number }[]
}

export function calculerMesures(devis: { id: string; avenantDe?: string; origineId?: string }[], pdfLe: Record<string, number>, maintenant = Date.now(), semaines = 4): Mesures {
  const durees = devis
    .filter((d) => !d.origineId && !d.avenantDe && pdfLe[d.id] !== undefined)
    .map((d) => pdfLe[d.id]! - dateDeCreation(d.id))
    .filter((ms) => ms >= 0 && ms <= SESSION_MAX_MS)
    .sort((a, b) => a - b)
  const milieu = Math.floor(durees.length / 2)
  const mediane = durees.length === 0 ? null : durees.length % 2 ? durees[milieu]! : Math.round((durees[milieu - 1]! + durees[milieu]!) / 2)

  const jour = (ms: number) => new Date(ms).toISOString().slice(0, 10)
  const lundiCourant = lundiDe(jour(maintenant))
  const debuts = Array.from({ length: semaines }, (_, i) => new Date(Date.parse(`${lundiCourant}T00:00:00Z`) - (semaines - 1 - i) * 7 * 86_400_000).toISOString().slice(0, 10))
  const parSemaine = debuts.map((lundi) => ({ lundi, devis: 0 }))
  for (const d of devis) {
    if (d.avenantDe) continue // un avenant n'est pas un nouveau devis
    const semaine = parSemaine.find((s) => s.lundi === lundiDe(jour(dateDeCreation(d.id))))
    if (semaine) semaine.devis++
  }
  return { mesures: durees.length, medianeMs: mediane, partRapide: durees.length ? durees.filter((ms) => ms < SEUIL_RAPIDE_MS).length / durees.length : null, parSemaine }
}

export async function lireMesures(base: BaseOutbox, maintenant = Date.now()): Promise<Mesures> {
  const [devis, notes] = await Promise.all([lister(base, 'devis'), base.meta.where('cle').startsWith('mesure:pdf:').toArray()])
  const pdfLe = Object.fromEntries(notes.map((n) => [n.cle.slice('mesure:pdf:'.length), n.valeur as number]))
  return calculerMesures(devis, pdfLe, maintenant)
}
